import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { mutate } from "swr";
import { Skeleton } from "~/components/ui/skeleton";
import { useProjectContext } from "~/pages/manifold-nextgen/components/projects/useProjectContext";
import {
  ConversationConnect,
  conversationAguiUrl,
  conversationConnectPath,
} from "~/services/conversations";
import { ProjectConversation, projectConversationsPath } from "~/services/projects";
import { apiClient } from "~/utils/apiClient";
import { Chat } from "./Chat";
import { ConversationProvider } from "./ConversationContext";
import { ReplayHttpAgent } from "./ReplayHttpAgent";
import { TopProgressBar } from "./TopProgressBar";
import { Composer } from "./composer/Composer";
import { useConnectors } from "./connectors/useConnectors";
import { useSkills } from "./skills/useSkills";
import { useConversationName } from "./useConversationName";

const AGENT_NAME = "default";

// Router state passed when navigating into a conversation. `isNew` is set by the
// New Session flow so the route can show the centered empty-state skeleton, and
// `initialMessage` carries the query typed on the splash to send as the first turn.
interface ConversationRouteState {
  isNew?: boolean;
  initialMessage?: string;
}

export const ConversationRoute = () => {
  const { conversationId, projectId } = useParams<{
    conversationId: string;
    projectId: string;
  }>();
  // Set by the New Session flow on navigation. A brand-new conversation is
  // always empty, so we can show the centered empty-state skeleton while
  // /connect loads instead of the bottom-composer layout — which otherwise
  // flashes before snapping to the centered view once the (empty) snapshot
  // arrives.
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as ConversationRouteState | null;
  const isNewConversation = Boolean(routeState?.isNew);
  // Capture the splash query once. Router state lives on the history entry, so
  // without clearing it a back/forward remount of this route would resubmit the
  // same message. We hold the captured value here and strip it from history.
  const [initialMessage] = useState(() => routeState?.initialMessage);
  useEffect(() => {
    if (routeState?.initialMessage) {
      navigate(location.pathname, { replace: true, state: { isNew: isNewConversation } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // The /connect payload is a one-shot session bootstrap, not a cacheable
  // resource: once the agent is mounted, all state updates flow through SSE
  // and never back into the snapshot. We deliberately bypass SWR here so a
  // later visit to the same conversation always refetches and never rebuilds
  // the agent from a stale snapshot.
  const [connect, setConnect] = useState<ConversationConnect | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);

  useEffect(() => {
    if (!conversationId) return;
    const controller = new AbortController();
    setConnect(undefined);
    setError(undefined);
    apiClient<ConversationConnect>(conversationConnectPath(conversationId), {
      signal: controller.signal,
    })
      .then((data) => {
        if (!controller.signal.aborted) setConnect(data);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err);
      });
    return () => controller.abort();
  }, [conversationId]);

  if (!conversationId) return null;

  if (error) {
    return (
      <div
        role="alert"
        className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load conversation</p>
      </div>
    );
  }

  if (!connect) {
    if (isNewConversation) return <NewConversationSkeleton />;
    return (
      <div
        className="relative flex flex-1 flex-col overflow-hidden"
        role="status"
        aria-label="Loading conversation"
        aria-busy="true"
      >
        <TopProgressBar />
        <div className="flex-1 overflow-y-auto px-6 py-4" />
        <div className="px-4 pb-4">
          <Composer disabled onSubmit={() => {}} />
        </div>
      </div>
    );
  }

  // `key={conversationId}` ensures Conversation (and its agent) is remounted
  // only when the user switches conversations.
  return (
    <Conversation
      key={conversationId}
      conversationId={conversationId}
      projectId={projectId}
      initial={connect}
      initialMessage={initialMessage}
    />
  );
};

// Loading placeholder for a brand-new session, mirroring Chat's centered empty
// state (agent header, composer, then starter prompts) so the view doesn't
// shift when the real empty conversation renders.
const NewConversationSkeleton = () => (
  <div
    className="relative flex flex-1 flex-col overflow-hidden"
    role="status"
    aria-label="Starting session"
    aria-busy="true"
  >
    <TopProgressBar />
    <div className="flex flex-1 flex-col items-center justify-center gap-8 overflow-y-auto p-8">
      <div className="flex flex-col items-center gap-4">
        <Skeleton className="size-20 rounded-full" />
        <div className="flex flex-col items-center gap-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
      </div>
      <Skeleton className="h-24 w-full max-w-2xl rounded-2xl" />
      <div className="flex w-full max-w-2xl flex-col gap-2">
        <div className="flex gap-2">
          <Skeleton className="h-9 flex-1 rounded-full" />
          <Skeleton className="h-9 flex-1 rounded-full" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 flex-1 rounded-full" />
          <Skeleton className="h-9 flex-1 rounded-full" />
        </div>
      </div>
    </div>
  </div>
);

interface ConversationProps {
  conversationId: string;
  projectId: string | undefined;
  // Snapshot from /connect taken at mount time. The agent is the source
  // of truth from here on; subsequent /connect responses are ignored.
  initial: ConversationConnect;
  // Query typed on the New Session splash, sent as the first turn on mount.
  initialMessage?: string;
}

const Conversation = ({
  conversationId,
  projectId,
  initial,
  initialMessage,
}: ConversationProps) => {
  const conversationAgent = initial.agent;
  const { attachedAgentsWithDetails, attachedDatasets } = useProjectContext();
  const fullAgent = attachedAgentsWithDetails?.find((a) => a.id === conversationAgent?.id);
  // scope_id → dataset name, so the picker can show which dataset a DATASET
  // skill was registered from.
  const datasetNames = useMemo(
    () => Object.fromEntries((attachedDatasets ?? []).map((d) => [d.id, d.title])),
    [attachedDatasets]
  );
  // The dataset(s) actually backing this conversation's context, from the
  // /connect snapshot — not the project's attached-dataset list, which is a
  // broader, separately-ordered set that drifts out of sync with the
  // conversation's real scope.
  const contextDatasetNames = initial.dataset_names;
  const agent = useMemo(
    () =>
      new ReplayHttpAgent({
        agentId: AGENT_NAME,
        threadId: conversationId,
        url: conversationAguiUrl(conversationId),
        headers: { Accept: "text/event-stream" },
        initialMessages: initial.messages,
        initialState: initial.state,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot at mount
    [conversationId]
  );

  // Patch the project conversations cache so the sidebar reflects the new
  // display name immediately — no refetch, no flicker. Backend has already
  // persisted by the time the event fires.
  const onConversationName = useCallback(
    (eventConversationId: string, displayName: string) => {
      if (!projectId) return;
      void mutate(
        projectConversationsPath(projectId),
        (current: { items: ProjectConversation[] } | undefined) => {
          if (!current) return current;
          return {
            ...current,
            items: current.items.map((c) =>
              c.id === eventConversationId ? { ...c, display_name: displayName } : c
            ),
          };
        },
        { revalidate: false }
      );
    },
    [projectId]
  );
  useConversationName(agent, onConversationName);

  const { skills } = useSkills(conversationId);
  const {
    connectors,
    selectedConnectorNames,
    toggleConnector,
    mcpGuidelines,
    forwardedConnectors,
    isLoading: connectorsLoading,
  } = useConnectors(conversationAgent?.id, projectId);

  return (
    <ConversationProvider conversationId={conversationId}>
      <Chat
        agent={agent}
        conversationId={conversationId}
        conversationAgent={conversationAgent}
        starterPrompts={fullAgent?.starter_prompts ?? []}
        resumeInFlight={initial.status === "MESSAGE_IN_PROGRESS"}
        skills={skills}
        datasetNames={datasetNames}
        connectors={connectors}
        enabledConnectorNames={selectedConnectorNames}
        onToggleConnector={toggleConnector}
        mcpGuidelines={mcpGuidelines}
        forwardedConnectors={forwardedConnectors}
        contextDatasetNames={contextDatasetNames}
        initialMessage={initialMessage}
        // Hold the lazy first turn until the connector grants AND the
        // MCP-guidelines acceptance have settled. While `accepted` is null the
        // forwarded allow-list is an empty list, which would drop the splash's
        // connector selection from the opening message.
        connectorsLoading={connectorsLoading || mcpGuidelines.accepted === null}
      />
    </ConversationProvider>
  );
};
